import * as React from "react";

/**
 * AppearanceProvider — single source of truth for every visual axis the
 * theme customizer panel exposes (matches the user-supplied reference
 * screenshots packages/design-tokens/references/Screenshot 003825.jpg
 * and 003847.jpg):
 *
 *   - Color mode    (light / dark / system)
 *   - Color palette (default / ocean-breeze / forest-whisper / sunset-glow / slate-pro / custom)
 *   - Custom color  (user-picked hex when palette === "custom")
 *   - Radius scale  (sm / md / lg / xl / full)
 *   - UI scale      (xs / sm / md / lg)
 *   - Sidebar mode  (default / icon)
 *   - Content layout(full / centered)
 *
 * Each axis is reflected on <html> as a `data-*` attribute so CSS in
 * tokens.css / palettes.css cascades automatically without re-render.
 * All choices persist to a single `eos:appearance` localStorage entry.
 */

export type ColorMode = "light" | "dark" | "system";
export type ResolvedColorMode = "light" | "dark";
export type Palette =
  | "default"
  | "ocean-breeze"
  | "forest-whisper"
  | "sunset-glow"
  | "slate-pro"
  | "custom";
export type Radius = "sm" | "md" | "lg" | "xl" | "full";
export type Scale = "xs" | "sm" | "md" | "lg";
export type SidebarMode = "default" | "icon";
export type ContentLayout = "full" | "centered";

export interface Appearance {
  mode: ColorMode;
  palette: Palette;
  radius: Radius;
  scale: Scale;
  sidebar: SidebarMode;
  layout: ContentLayout;
  /**
   * User-picked primary color, in hex (e.g. "#5b6cf2"). Only used when
   * `palette === "custom"`. We persist this even when a different
   * preset is active so the user can flip back to "Custom" without
   * re-picking. `null` falls back to a sensible cobalt default.
   */
  customColor: string | null;
}

interface AppearanceContextValue extends Appearance {
  resolvedMode: ResolvedColorMode;
  set: (patch: Partial<Appearance>) => void;
  reset: () => void;
}

const STORAGE_KEY = "eos:appearance";

const DEFAULTS: Appearance = {
  mode: "system",
  palette: "default",
  radius: "md",
  scale: "md",
  sidebar: "default",
  layout: "full",
  customColor: null,
};

/**
 * Default seed for the custom-color picker — a friendly cobalt/indigo
 * that reads well in both light and dark modes. Matches the brand
 * primary in tokens.css so picking "Custom" with the seed value still
 * looks intentional.
 */
export const DEFAULT_CUSTOM_COLOR = "#5b6cf2";

/**
 * Compute --color-primary + --color-primary-foreground for a user-picked
 * hex color. The foreground tone (text on top of primary) flips between
 * near-white and near-black based on perceived luminance — calculated
 * cheaply via a Rec. 709 luma approximation on the sRGB channels.
 *
 * The rest of the design-tokens stack uses oklch for perceptual
 * uniformity; we mix-pass the sRGB hex through unchanged because
 * Tailwind/CSS reads the raw value off `--color-primary`.
 */
function customColorVars(hex: string): { primary: string; foreground: string } {
  const cleanHex = hex.startsWith("#") ? hex.slice(1) : hex;
  const expanded =
    cleanHex.length === 3
      ? cleanHex
          .split("")
          .map((c) => c + c)
          .join("")
      : cleanHex.padEnd(6, "0").slice(0, 6);
  const r = parseInt(expanded.slice(0, 2), 16) / 255;
  const g = parseInt(expanded.slice(2, 4), 16) / 255;
  const b = parseInt(expanded.slice(4, 6), 16) / 255;
  const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  const foreground = luma > 0.55 ? "oklch(0.16 0.02 260)" : "oklch(0.99 0 0)";
  return {
    primary: `#${expanded}`,
    foreground,
  };
}

const AppearanceContext = React.createContext<AppearanceContextValue | null>(null);

function isAppearance(v: unknown): v is Partial<Appearance> {
  return typeof v === "object" && v !== null;
}

function readStored(): Appearance {
  if (typeof window === "undefined") return DEFAULTS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw);
    if (!isAppearance(parsed)) return DEFAULTS;
    return { ...DEFAULTS, ...parsed };
  } catch {
    return DEFAULTS;
  }
}

function resolveMode(mode: ColorMode): ResolvedColorMode {
  if (mode !== "system") return mode;
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyToHtml(a: Appearance, resolved: ResolvedColorMode): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.setAttribute("data-theme", resolved);
  root.setAttribute("data-palette", a.palette);
  root.setAttribute("data-radius", a.radius);
  root.setAttribute("data-scale", a.scale);
  root.setAttribute("data-sidebar", a.sidebar);
  root.setAttribute("data-layout", a.layout);

  // Inline-inject --color-primary when palette === "custom". When the
  // user flips back to a preset, we clear the inline override so the
  // CSS rules in palettes.css take over again. Inline style wins over
  // CSS rules so we don't need a data-palette="custom" rule anywhere.
  if (a.palette === "custom") {
    const hex = a.customColor ?? DEFAULT_CUSTOM_COLOR;
    const { primary, foreground } = customColorVars(hex);
    root.style.setProperty("--color-primary", primary);
    root.style.setProperty("--color-primary-foreground", foreground);
  } else {
    root.style.removeProperty("--color-primary");
    root.style.removeProperty("--color-primary-foreground");
  }
}

export interface AppearanceProviderProps {
  children: React.ReactNode;
  /** Override the default palette (per-portal: institution-admin vs super-admin). */
  defaultPalette?: Palette;
}

export function AppearanceProvider({ children, defaultPalette }: AppearanceProviderProps) {
  const initial = React.useMemo<Appearance>(
    () => ({ ...DEFAULTS, ...(defaultPalette ? { palette: defaultPalette } : {}) }),
    [defaultPalette],
  );

  const [state, setState] = React.useState<Appearance>(initial);
  const [resolvedMode, setResolvedMode] = React.useState<ResolvedColorMode>(() =>
    resolveMode(initial.mode),
  );

  // Hydrate from localStorage post-mount (the inline pre-paint script in
  // index.html already applied the data-* attributes for first paint).
  React.useEffect(() => {
    const stored = readStored();
    const merged: Appearance = {
      ...stored,
      // Honour the per-portal default palette ONLY if user hasn't picked one.
      palette:
        stored.palette === DEFAULTS.palette && defaultPalette ? defaultPalette : stored.palette,
    };
    setState(merged);
    const r = resolveMode(merged.mode);
    setResolvedMode(r);
    applyToHtml(merged, r);
  }, [defaultPalette]);

  // Re-resolve when OS scheme flips and user is on `system`.
  React.useEffect(() => {
    if (state.mode !== "system" || typeof window === "undefined") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      const r: ResolvedColorMode = mql.matches ? "dark" : "light";
      setResolvedMode(r);
      applyToHtml(state, r);
    };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [state]);

  const set = React.useCallback((patch: Partial<Appearance>) => {
    setState((prev) => {
      const next = { ...prev, ...patch };
      const r = resolveMode(next.mode);
      setResolvedMode(r);
      applyToHtml(next, r);
      if (typeof window !== "undefined") {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      }
      return next;
    });
  }, []);

  const reset = React.useCallback(() => {
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(STORAGE_KEY);
    }
    const next: Appearance = {
      ...DEFAULTS,
      ...(defaultPalette ? { palette: defaultPalette } : {}),
    };
    setState(next);
    const r = resolveMode(next.mode);
    setResolvedMode(r);
    applyToHtml(next, r);
  }, [defaultPalette]);

  const value = React.useMemo<AppearanceContextValue>(
    () => ({ ...state, resolvedMode, set, reset }),
    [state, resolvedMode, set, reset],
  );

  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
}

export function useAppearance(): AppearanceContextValue {
  const ctx = React.useContext(AppearanceContext);
  if (!ctx) throw new Error("useAppearance must be used inside <AppearanceProvider>");
  return ctx;
}
